/**
 * Internal Metrics View — three numbers, one table.
 *
 * DATA NOTE: despite its name, /admin/user-transactions is a v1-shaped facade
 * over the v2 tables and returns ONE ROW PER ENTRY GROUP. So every metric here
 * counts groups — the same unit utilization uses — and the two pages are
 * directly comparable. Group is also the right unit on the merits: entry counts
 * move when the grouper is retuned, which is system behaviour, not attorney
 * behaviour, and nobody should be targeted against a number that shifts for
 * that reason.
 *
 *   Utilization  exported Hourglass hours / billed hours in the firm's
 *                practice-management system. Target 95%.
 *   Deleted      deleted / everything Hourglass generated. Hand-created
 *                entries are excluded from the denominator.
 *   Manual       hand-created / everything exported.
 *
 * The page answers "where is each timekeeper against each target" in one pass.
 * Everything else — per-field corrections, provenance detail — lives inside an
 * expanded row rather than competing for the top level.
 *
 * Access: the /internal/metrics route in InternalPage gates this with the
 * strict check (no DEV override) before mounting, so the hooks below never run
 * for a user who will be redirected. All client-side: the endpoints must
 * enforce the same rule server-side.
 */

import { useMemo, useState } from "react";
import { useQueryState } from "nuqs";
import { Card } from "@/shared/components/ui/card";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/shared/components/ui/tooltip";
import { cn } from "@/shared/lib/utils";
import { DatePicker } from "@/v2/components/date-picker/DatePicker";
import { isCompleteDateRange } from "@/v2/lib/dates";
import { useDateParams } from "@/shared/hooks/useDateParams";
import { useUtilization } from "@/features/admin/hooks/useAdminData";
import loaderStyles from "@/shared/components/LoadingPopup/LoadingPopup.module.css";
import { useMetrics } from "../metrics/useMetrics";
import {
	captured,
	deletionRate,
	formatRate,
	manualRate,
	measureOf,
	mergeBuckets,
	rate,
} from "../metrics/aggregate";
import type { Measure, MetricStatus, MetricsBucket } from "../metrics/types";
import {
	METRIC_TARGETS,
	PROVISIONAL_TARGETS,
	bandFor,
	comparisonBand,
	STATUS_CLASS,
	STATUS_LABEL,
	gapToTarget,
	statusOf,
	type MetricTarget,
} from "../metrics/targets";
import { CorrectionBreakdown } from "../metrics/components/CorrectionBreakdown";
import { SatisfactionCell } from "../metrics/components/SatisfactionCell";
import { SatisfactionHistoryDialog } from "../metrics/components/SatisfactionDialogs";
import { NoteCard, NoteTab } from "../metrics/components/NoteCard";
import {
	SATISFACTION_HINTS,
	SATISFACTION_SCORES,
	satisfactionRank,
	useSatisfaction,
} from "../metrics/useSatisfaction";
import { HelpCircle } from "lucide-react";
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from "@/shared/components/ui/popover";
import {
	BreakdownTable,
	type BreakdownCell,
	type BreakdownColumn,
} from "../metrics/components/BreakdownTable";
import { Button } from "@/shared/components/ui/button";

/**
 * Start of the default window: 7 September 2026 through today. A fixed anchor
 * rather than a rolling window, so it widens on its own. Month is 0-indexed.
 */
const DEFAULT_RANGE_START = { year: 2026, month: 8, day: 7 };

function defaultRange(): { from: Date; to: Date } {
	const from = new Date(
		DEFAULT_RANGE_START.year,
		DEFAULT_RANGE_START.month,
		DEFAULT_RANGE_START.day,
	);
	const today = new Date();
	// Guard the inverted case: a backwards range would send the API a start
	// later than its end.
	return { from, to: today < from ? from : today };
}

function toApiDate(d: Date): string {
	const y = d.getFullYear();
	const m = String(d.getMonth() + 1).padStart(2, "0");
	const day = String(d.getDate()).padStart(2, "0");
	return `${y}-${m}-${day}`;
}

/** The three values for one timekeeper (or the firm). */
interface MetricValues {
	utilization: number | null;
	deletionRate: number | null;
	manualRate: number | null;
}

// ─────────────────────────────────────────────
// Headline
// ─────────────────────────────────────────────

function Headline({
	target,
	value,
	note,
}: {
	target: MetricTarget;
	value: number | null;
	note?: string;
}) {
	const status = statusOf(value, target);
	const gap = gapToTarget(value, target);
	const provisional = PROVISIONAL_TARGETS.has(target.key);

	return (
		<Card className="p-4">
			<div className="flex items-baseline justify-between gap-2">
				<Tooltip>
					<TooltipTrigger asChild>
						<span className="cursor-help text-xs font-medium uppercase tracking-wide text-muted-foreground">
							{target.label}
						</span>
					</TooltipTrigger>
					<TooltipContent className="max-w-xs">{target.help}</TooltipContent>
				</Tooltip>
				<span className="text-[11px] text-muted-foreground">
					target {target.direction === "higher" ? "≥" : "≤"} {target.target}%
					{provisional && "*"}
				</span>
			</div>

			<div className="mt-1 flex items-baseline gap-2">
				<span className="text-3xl font-semibold tabular-nums">
					{formatRate(value)}
				</span>
				{gap !== null && (
					<span className={cn("text-xs font-medium", STATUS_CLASS[status])}>
						{gap >= 0 ? STATUS_LABEL.met : `${Math.abs(gap).toFixed(1)} pts away`}
					</span>
				)}
			</div>

			<div className="mt-1 text-[11px] text-muted-foreground">
				{note ?? (target.deadline ? `by ${target.deadline}` : "no date set")}
			</div>
		</Card>
	);
}

// ─────────────────────────────────────────────
// View
// ─────────────────────────────────────────────

export default function MetricsView() {
	const { dateRange, setDateRange } = useDateParams();
	const [expanded, setExpanded] = useState<string | null>(null);
	const [historyFor, setHistoryFor] = useState<string | null>(null);
	const [noteOpenFor, setNoteOpenFor] = useState<string | null>(null);
	const [measure, setMeasure] = useState<Measure>("units");
	// Onboarded first by default — the question the page opens on is who is
	// live, not who is outstanding.
	const [onboardedFirst, setOnboardedFirst] = useState(true);

	// Temporary, reversible exclusions. Held in the URL so a filtered view is
	// shareable and survives a refresh, and so "undo" is just removing an id.
	const [excludedRaw, setExcludedRaw] = useQueryState("exclude");
	const excludedIds = useMemo(
		() => new Set((excludedRaw ?? "").split(",").filter(Boolean)),
		[excludedRaw],
	);

	function toggleExcluded(id: string) {
		const next = new Set(excludedIds);
		if (next.has(id)) next.delete(id);
		else next.add(id);
		setExcludedRaw(next.size > 0 ? [...next].join(",") : null, {
			history: "replace",
		});
	}

	// `useDateParams` reports an empty range for a single day and for an
	// untouched URL alike, so fall back to the fixed default whenever there is
	// no complete range. Single-day picks route through `setDateRange` so they
	// always write both params.
	const hasExplicitDates = isCompleteDateRange(dateRange);
	const range = useMemo(
		() =>
			isCompleteDateRange(dateRange)
				? { from: dateRange.from, to: dateRange.to }
				: defaultRange(),
		[dateRange],
	);

	const { summary, isLoading, error } = useMetrics(range.from, range.to);

	// Utilization comes from the billing comparison. Reused rather than
	// recomputed so this page agrees with the admin utilization page. Both read
	// time_entry_groups, but with one filter difference: utilization requires
	// only duration_s > 0, while the endpoint behind the other two metrics also
	// requires a non-empty narrative — so totals can differ slightly.
	const {
		utilization,
		loading: utilLoading,
		error: utilError,
	} = useUtilization(toApiDate(range.from), toApiDate(range.to));

	/** prof_id -> utilization %, null when the firm has no billing data. */
	const utilByProf = useMemo(() => {
		const map = new Map<string, number | null>();
		for (const a of utilization?.attorneys ?? []) {
			// total_billing_hours = 0 yields utilization_pct = 0 from the API, which
			// is indistinguishable from real zero utilization. It nearly always
			// means "no billing rows synced", so surface it as unknown.
			map.set(a.prof_id, a.total_billing_hours > 0 ? a.utilization_pct : null);
		}
		return map;
	}, [utilization]);

	const includedBuckets = useMemo(
		() => (summary?.perUser ?? []).filter((b) => !excludedIds.has(b.profId)),
		[summary, excludedIds],
	);

	/**
	 * Firm rollup over the INCLUDED timekeepers only. Re-totalled from the
	 * per-user buckets rather than read off `summary.firm`, which always covers
	 * everyone.
	 */
	const includedFirm = useMemo(
		() => mergeBuckets(includedBuckets),
		[includedBuckets],
	);

	// Utilization is re-totalled the same way: sum the included attorneys'
	// hours rather than reusing the API's firm-wide percentage, which cannot
	// account for an exclusion made here.
	const firmUtilization = useMemo(() => {
		const rows = (utilization?.attorneys ?? []).filter(
			(a) => !excludedIds.has(a.prof_id),
		);
		const hg = rows.reduce((sum, a) => sum + a.hourglass_hours, 0);
		const billed = rows.reduce((sum, a) => sum + a.total_billing_hours, 0);
		return billed > 0 ? (hg / billed) * 100 : null;
	}, [utilization, excludedIds]);

	const bucketsById = useMemo(() => {
		const map = new Map<string, MetricsBucket>();
		for (const b of summary?.perUser ?? []) map.set(b.profId, b);
		return map;
	}, [summary]);

	const tableRows = useMemo(
		() =>
			(summary?.perUser ?? []).map((b) => ({
				id: b.profId,
				name: b.profName,
			})),
		[summary],
	);

	const {
		scores,
		record: recordSatisfaction,
		saveNote,
		setOnboarded,
		isSaving: savingSatisfaction,
		isSavingNote,
		saveError: satisfactionSaveError,
	} = useSatisfaction(tableRows.map((r) => r.id));

	const onboardedCount = useMemo(
		() => tableRows.filter((r) => scores[r.id]?.onboarded === true).length,
		[tableRows, scores],
	);

	function nameFor(profId: string | null): string {
		return tableRows.find((r) => r.id === profId)?.name ?? "";
	}

	/**
	 * Columns. The three "Share of captured" percentages sum to 100%; the other
	 * two have external denominators, which is what the header groups signal.
	 */
	const columns: BreakdownColumn[] = useMemo(() => {
		function bucketFor(id: string): MetricsBucket | undefined {
			return bucketsById.get(id);
		}

		/**
		 * The three "share of captured" columns differ only in which status they
		 * count and how they are coloured; everything else is one denominator.
		 *
		 * Tracked and Exported are banded against EACH OTHER rather than against
		 * a threshold — the question is whether this person ships more than they
		 * sit on, which no fixed cutoff can express.
		 */
		function compositionCell(id: string, status: MetricStatus): BreakdownCell {
			const b = bucketFor(id);
			if (!b) return { pct: null, numerator: null, denominator: null };

			const d = captured(b, measure);
			const n = measureOf(b.byStatus[status], measure);
			const pct = rate(n, d);

			const band =
				status === "deleted"
					? bandFor("deletedShare", pct)
					: comparisonBand(
							rate(measureOf(b.byStatus.exported, measure), d),
							rate(measureOf(b.byStatus.tracked, measure), d),
						);

			return { pct, numerator: n, denominator: d, band };
		}

		return [
			{
			key: "tracked",
				label: "Tracked",
				group: "Share of captured",
				help: "Captured but not yet exported, as a share of everything captured. Reviewed entries are counted here. Coloured against Exported: green when more is shipping than sitting, red when it is the other way round.",
				cell: (id) => compositionCell(id, "tracked"),
			},
			{
				key: "exportedShare",
				label: "Exported",
				group: "Share of captured",
				help: "Exported as a share of everything captured. Coloured against Tracked: green when exported leads, red when tracked does, orange on a tie. For the comparison against billed time, see Utilization.",
				cell: (id) => compositionCell(id, "exported"),
			},
			{
				key: "deletedShare",
				label: "Deleted",
				group: "Share of captured",
				help: "Deleted as a share of everything captured. Counts group-level deletions only — an entry deleted out of a surviving group is not included, so this is a floor.",
				cell: (id) => compositionCell(id, "deleted"),
			},
			{
				key: "utilization",
				label: "Utilization",
				group: "vs billing",
				alwaysShowFraction: true,
				// Billing has no entry count, so this column stays in hours whatever
				// the Units/Entries toggle says.
				fractionMeasure: "units",
				help: "Exported Hourglass hours over the attorney's billed hours in the practice-management system. Always hours-based — there is no entry count on the billing side — so this column does not change with the Units/Entries toggle.",
				cell: (id) => {
					const pct = utilByProf.get(id) ?? null;
					const att = utilization?.attorneys.find((a) => a.prof_id === id);
					if (pct === null) {
						return {
							pct: null,
							numerator: null,
							denominator: null,
							absentReason: "no billing data",
						};
					}
					return {
						pct,
						numerator: att?.hourglass_hours ?? null,
						denominator: att?.total_billing_hours ?? null,
						band: bandFor("utilization", pct),
					};
				},
			},
			{
				key: "manual",
				label: "Manual",
				group: "Quality",
				alwaysShowFraction: true,
				help: "Hand-created as a share of everything exported. A group's source is read from its first entry, so a group mixing hand-created and captured work is classified by whichever came first.",
				cell: (id) => {
					const b = bucketFor(id);
					if (!b) return { pct: null, numerator: null, denominator: null };
					const n =
						measure === "count"
							? b.headline.exportedManual
							: b.headline.exportedManualSeconds / 3600;
					const d =
						measure === "count"
							? b.headline.exported
							: b.headline.exportedSeconds / 3600;
					const pct = rate(n, d);
					return {
						pct,
						numerator: n,
						denominator: d,
						band: bandFor("manual", pct),
					};
				},
			},
			{
				key: "satisfaction",
				label: "Satisfaction",
				group: "Feedback",
				help: "Manually recorded, not measured. Sorts best-first: \"Very disappointed\" means they would fight to keep Hourglass.",
				// Ascending, because the sort value is a rank where 1 is best. Every
				// other column is a percentage whose high end is the interesting one.
				initialDesc: false,
				groupAccessory: (
					<Popover>
						<PopoverTrigger asChild>
							<button
								type="button"
								aria-label="How the satisfaction scale works"
								className="text-muted-foreground hover:text-foreground"
							>
								<HelpCircle className="size-3.5" />
							</button>
						</PopoverTrigger>
						<PopoverContent align="end" className="w-72 text-xs">
							<p className="font-medium text-foreground">
								If Hourglass went away, how would users feel?
							</p>
						<ul className="mt-2 space-y-1.5">
								{SATISFACTION_SCORES.map((score) => (
									<li key={score}>
										<span
											className={cn(
												"font-medium",
												// Best two are green; the array is ordered best-first.
												SATISFACTION_SCORES.indexOf(score) < 2
													? "text-green-700 dark:text-green-400"
													: "text-red-700 dark:text-red-400",
											)}
										>
											{score}
										</span>
										<span className="ml-1.5 text-muted-foreground">
											{SATISFACTION_HINTS[score]}
										</span>
									</li>
								))}
							</ul>
							<p className="mt-2 text-muted-foreground">
								Disappointed is good — it means losing Hourglass would hurt.
								Click a rating to change it. A new entry is only recorded when
								the rating actually changes.
							</p>
						</PopoverContent>
					</Popover>
				),
				// Sorting needs a NUMBER, and it cannot be the rating itself:
				// alphabetical order puts "Happy" first, which is exactly backwards.
				// The rank is 1 for the best outcome, so the column sorts best-first.
				cell: (id) => {
					// `current` is null for a timekeeper who has notes but no rating —
					// the optional chain has to cover it, not just the map lookup.
					const rank = satisfactionRank(scores[id]?.current?.score ?? null);
					return { pct: rank, numerator: null, denominator: null };
				},
				renderCell: (id) => (
					<SatisfactionCell
						pair={scores[id]}
						saving={savingSatisfaction}
						onRecord={(score) => void recordSatisfaction(id, score)}
						onOpenHistory={() => setHistoryFor(id)}
					/>
				),
			},
		];
	}, [
		bucketsById,
		measure,
		utilByProf,
		utilization,
		scores,
		savingSatisfaction,
		recordSatisfaction,
	]);

	const loading = isLoading || utilLoading;
	const firmValues: MetricValues | null = summary
		? {
				utilization: firmUtilization,
				deletionRate: deletionRate(includedFirm),
				manualRate: manualRate(includedFirm),
			}
		: null;

	const syncNote = utilization?.billing_last_synced
		? `billing synced ${utilization.billing_last_synced.slice(0, 10)}`
		: "billing never synced";

	return (
		<div className="mx-auto max-w-6xl p-6">
			<header className="mb-4 flex flex-wrap items-center justify-between gap-3">
				<div>
					<h1 className="text-xl font-semibold">Metrics</h1>
					<p className="mt-0.5 text-xs text-muted-foreground">
						{hasExplicitDates ? "Selected range" : "Since 7 Sep"} ·{" "}
						{tableRows.length} timekeeper{tableRows.length === 1 ? "" : "s"}
						{excludedIds.size > 0 && (
							<>
								{" · "}
								<span className="text-amber-700 dark:text-amber-400">
									{excludedIds.size} excluded from totals
								</span>{" "}
								<button
									type="button"
									onClick={() => setExcludedRaw(null, { history: "replace" })}
									className="underline underline-offset-2 hover:text-foreground"
								>
									reset
								</button>
							</>
						)}
					</p>
				</div>
				<DatePicker
					className="w-fit"
					selectionMode="range"
					dateRange={range}
					onDateRangeChange={setDateRange}
					onDateChange={(d) => setDateRange({ from: d, to: d })}
				/>
			</header>

			{loading && (
				<div className="flex min-h-[40vh] items-center justify-center">
					<div className={loaderStyles.loader} />
				</div>
			)}

			{!loading && error && (
				<Card className="p-5 text-sm text-destructive">
					Could not load metrics: {error}
				</Card>
			)}

			{!loading && !error && summary && firmValues && (
				<>
					<div className="grid gap-3 sm:grid-cols-3">
						<Headline
							target={METRIC_TARGETS.utilization}
							value={firmValues.utilization}
							note={syncNote}
						/>
						<Headline
							target={METRIC_TARGETS.deletionRate}
							value={firmValues.deletionRate}
						/>
						<Headline
							target={METRIC_TARGETS.manualRate}
							value={firmValues.manualRate}
						/>
					</div>

					{satisfactionSaveError && (
						<p className="mt-2 text-xs text-destructive">
							{/* Names no single operation: `saveError` is one channel shared
							    by the rating, the note and the onboarding toggle, so
							    naming the score mislabelled the other two. */}
							Could not save: {satisfactionSaveError}
						</p>
					)}

					{utilError && (
						<p className="mt-2 text-xs text-amber-700 dark:text-amber-400">
							Utilization unavailable: {utilError}
						</p>
					)}

					<div className="mt-4 flex items-center justify-end">
						<div className="inline-flex overflow-hidden rounded-md border">
							{(["units", "count"] as const).map((m) => (
								<Button
									key={m}
									type="button"
									variant="ghost"
									size="sm"
									onClick={() => setMeasure(m)}
									aria-pressed={measure === m}
									className={cn(
										"h-7 rounded-none px-3 text-xs",
										measure === m && "bg-accent text-accent-foreground",
									)}
								>
									{m === "units" ? "Units" : "Entries"}
								</Button>
							))}
						</div>
					</div>

					<div className="mt-2">
						<BreakdownTable
							partition={{
								// A timekeeper with no satisfaction row is absent from the
								// map, so this is `=== true`, not a truthiness check on a
								// field that may not be there at all.
								of: (id) => scores[id]?.onboarded === true,
								labels: { on: "Onboarded", off: "Not onboarded" },
								desc: onboardedFirst,
								onToggle: () => setOnboardedFirst((v) => !v),
								onToggleRow: (id) =>
									void setOnboarded(id, scores[id]?.onboarded !== true),
								// Denominator is every licensed timekeeper on the page. The
								// eye-exclusion is a view filter, not a licensing fact, so it
								// deliberately does not move this count.
								summary: `${onboardedCount} / ${tableRows.length} Onboarded`,
							}}
							excludedIds={excludedIds}
							onToggleExclude={toggleExcluded}
							trailingCell={(id) => (
								<NoteTab
									hasNote={!!scores[id]?.latestNote}
									isOpen={noteOpenFor === id}
									onToggle={() =>
										setNoteOpenFor(noteOpenFor === id ? null : id)
									}
								/>
							)}
							rowPanel={(id) =>
								noteOpenFor === id ? (
									<NoteCard
										name={nameFor(id)}
										savedNote={scores[id]?.latestNote?.note ?? ""}
										score={scores[id]?.current?.score ?? null}
										saving={isSavingNote}
										onSave={(note) => void saveNote(id, note)}
										onClose={() => setNoteOpenFor(null)}
									/>
								) : null
							}
							rows={tableRows}
							columns={columns}
							measure={measure}
							defaultSort="utilization"
							expandedId={expanded}
							onToggleExpand={(id) =>
								setExpanded(expanded === id ? null : id)
							}
							renderDetail={(id) => {
								const bucket = bucketsById.get(id);
								if (!bucket) return null;
								return (
									<div className="grid gap-4 lg:grid-cols-2">
										<div className="space-y-1 text-xs text-muted-foreground">
											<div>
												Generated{" "}
												<span className="font-medium text-foreground">
													{bucket.headline.generated}
												</span>{" "}
												· deleted{" "}
												<span className="font-medium text-foreground">
													{bucket.headline.generatedDeleted}
												</span>
											</div>
											<div>
												Exported{" "}
												<span className="font-medium text-foreground">
													{bucket.headline.exported}
												</span>{" "}
												· hand-created{" "}
												<span className="font-medium text-foreground">
													{bucket.headline.exportedManual}
												</span>
											</div>
											<div>
												Last active{" "}
												<span className="font-medium text-foreground">
													{bucket.lastActive?.slice(0, 10) ?? "never"}
												</span>
											</div>
										</div>
										<CorrectionBreakdown
											bucket={bucket}
											provenanceCoverage={summary.provenanceCoverage}
											v1ProvenanceCoverage={summary.v1ProvenanceCoverage}
											denominatorLabel="groups"
										/>
									</div>
								);
							}}
						/>
					</div>

					<div className="mt-3 space-y-1 text-[11px] text-muted-foreground">
						<p>
							* Provisional target — no measured baseline yet.
						</p>
						<p>
							A group is dated by its <strong>first entry&rsquo;s start
							time</strong>, not by when it was deleted or exported — so a
							group worked on the 10th and deleted on the 14th counts against
							the 10th. Deletions are attributed to when the work was captured,
							not when it was thrown away.
						</p>
						<p>
							All three metrics count entry <strong>groups</strong>, the same
							unit utilization uses. Two known gaps: deletions are counted at
							the group level only, so an entry deleted out of a surviving group
							is not included; and a group&rsquo;s capture source is read from
							its first entry, so a group mixing hand-created and captured work
							is classified by whichever entry came first.
						</p>
						{includedFirm.headline.missingDuration > 0 && (
							<p>
								{includedFirm.headline.missingDuration.toLocaleString()} group
								{includedFirm.headline.missingDuration === 1 ? "" : "s"} have no
								duration recorded — they are counted in Entries mode but
								contribute no hours, so Units totals understate by that much.
							</p>
						)}
						{includedFirm.headline.unclassified > 0 && (
							<p>
								{includedFirm.headline.unclassified.toLocaleString()} group
								{includedFirm.headline.unclassified === 1 ? "" : "s"} excluded
								from both rates — capture source unknown, most often timer-fill
								placeholders.
							</p>
						)}
					</div>

				</>
			)}
			<SatisfactionHistoryDialog
				profId={historyFor}
				profName={nameFor(historyFor)}
				onClose={() => setHistoryFor(null)}
			/>
		</div>
	);
}
